/**
 * Theme control + the canvas colour bridge (law 8: theme duality).
 *
 * WHY THIS FILE EXISTS: a <canvas> does not inherit CSS. Everything drawn into it is
 * painted with literal colour strings, so a chart styled "through tokens" will happily
 * keep its dark palette on a light page unless something explicitly re-reads the tokens
 * and repaints. That is the theme-duality bug this file prevents: `resolveChartColors()`
 * reads the CURRENT computed values of the CSS custom properties, and `onThemeChange()`
 * fires whenever they could have changed — including when the OS preference flips while
 * the user is on "system".
 */

import { ALPHAOPS_COLORS, type ChartColors } from "./chart/types";

export type ThemeChoice = "system" | "light" | "dark";
export type ResolvedTheme = "light" | "dark";

const STORAGE_KEY = "spx-hub.theme";

/** Read a CSS custom property off :root, resolved to its current computed value. */
function token(name: string, fallback: string): string {
  if (typeof window === "undefined") return fallback;
  const v = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  return v || fallback;
}

/**
 * Snapshot the design tokens into the chart engine's colour contract.
 * MUST be re-run after every theme change — the values are copies, not live references.
 */
export function resolveChartColors(): ChartColors {
  return {
    ...ALPHAOPS_COLORS,
    background: token("--surface", ALPHAOPS_COLORS.background),
    grid: token("--line", ALPHAOPS_COLORS.grid),
    priceUp: token("--up", ALPHAOPS_COLORS.priceUp),
    priceDown: token("--dn", ALPHAOPS_COLORS.priceDown),
    priceLine: token("--ink", ALPHAOPS_COLORS.priceLine),
    candleUp: token("--up", ALPHAOPS_COLORS.candleUp),
    candleDown: token("--dn", ALPHAOPS_COLORS.candleDown),
    vwap: token("--vwap", ALPHAOPS_COLORS.vwap),
    avwap: token("--flip", ALPHAOPS_COLORS.avwap),
    cvdPositive: token("--cvd-pos", ALPHAOPS_COLORS.cvdPositive),
    cvdNegative: token("--cvd-neg", ALPHAOPS_COLORS.cvdNegative),
    // Dealer level classes (SPX-signature overlay). Both are validated in BOTH themes by
    // PALETTE_VALIDATION_2026-08-06 — magnet 3.41 light / 5.30 dark, flip 5.19 / 4.51, all above
    // the 3:1 marks floor, worst all-pairs ΔE 8.0 light / 8.3 dark.
    //
    // ⚠ RULE-3 GAP, INHERITED AND STILL OPEN (HUDCHAT-e dlv 4, re-flagged to this seat by DESK-n4):
    // these token names are JS STRINGS. `check-tokens.mjs` RULE 3 only validates `var(--x)` inside
    // CSS, so renaming `--magnet` or `--flip` in tokens.css would break this overlay SILENTLY and
    // no gate would fire. The fallbacks below make that a wrong-colour bug rather than an invisible
    // one; they do not make it a caught one. Treat renames of these two as unguarded.
    levelMagnet: token("--magnet", ALPHAOPS_COLORS.levelMagnet),
    levelFlip: token("--flip", ALPHAOPS_COLORS.levelFlip),
    text: token("--ink", ALPHAOPS_COLORS.text),
    textMuted: token("--muted", ALPHAOPS_COLORS.textMuted),
    laneBg: token("--surface-2", ALPHAOPS_COLORS.laneBg),
  };
}

export function storedChoice(): ThemeChoice {
  if (typeof localStorage === "undefined") return "system";
  const v = localStorage.getItem(STORAGE_KEY);
  return v === "light" || v === "dark" ? v : "system";
}

function systemTheme(): ResolvedTheme {
  if (typeof window === "undefined") return "dark";
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

export function resolveTheme(choice: ThemeChoice): ResolvedTheme {
  return choice === "system" ? systemTheme() : choice;
}

/**
 * Apply a choice. On "system" the data-theme stamp is REMOVED rather than set to the
 * current system value — leaving a stale stamp behind is what makes "system" stop
 * tracking the OS after the user has toggled once.
 */
export function applyTheme(choice: ThemeChoice): ResolvedTheme {
  const root = document.documentElement;
  if (choice === "system") {
    root.removeAttribute("data-theme");
    localStorage.removeItem(STORAGE_KEY);
  } else {
    root.setAttribute("data-theme", choice);
    localStorage.setItem(STORAGE_KEY, choice);
  }
  return resolveTheme(choice);
}

/**
 * Subscribe to anything that can change the resolved theme: an explicit toggle here,
 * or the OS flipping underneath a "system" choice. Returns an unsubscribe function.
 */
export function onThemeChange(cb: (theme: ResolvedTheme) => void): () => void {
  const mq = window.matchMedia("(prefers-color-scheme: dark)");
  const onSystem = () => {
    // Only relevant while the user is actually on "system".
    if (storedChoice() === "system") cb(systemTheme());
  };
  mq.addEventListener("change", onSystem);

  // Our own toggle dispatches this; see ThemeToggle.svelte.
  const onExplicit = (e: Event) => cb((e as CustomEvent<ResolvedTheme>).detail);
  window.addEventListener("spx-theme", onExplicit);

  return () => {
    mq.removeEventListener("change", onSystem);
    window.removeEventListener("spx-theme", onExplicit);
  };
}

/** Call once, as early as possible, to avoid a flash of the wrong theme. */
export function initTheme(): ResolvedTheme {
  const choice = storedChoice();
  if (choice !== "system") document.documentElement.setAttribute("data-theme", choice);
  return resolveTheme(choice);
}
